import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { LayoutService } from '@core/services/layout-store.service';
import {
  UserPreferenceModel,
  UserPreferenceService,
  UserPreferenceThemeMode,
  UserPreferenceUpsertPayload,
} from '@core/services/user-preference.service';
import { LayoutThemeType } from '@/app/types/layout';
import { ToastrService } from 'ngx-toastr';
import { catchError, EMPTY, of, switchMap } from 'rxjs';

@Component({
  selector: 'app-theme-toggle',
  templateUrl: './theme-toggle.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThemeToggle {
  private readonly layoutStore = inject(LayoutService);
  private readonly userPreferenceService = inject(UserPreferenceService);
  private readonly toastr = inject(ToastrService);

  readonly buttonClass = input('btn btn-system');
  readonly iconClass = input('sa-icon sa-icon-2x');
  readonly iconViewBox = input('0 0 24 24');
  readonly ariaLabel = input('Toggle theme');
  readonly isDarkTheme = computed(
    () => this.layoutStore.state().theme === 'dark',
  );

  toggleTheme(): void {
    const nextTheme: LayoutThemeType =
      this.layoutStore.theme === 'light' ? 'dark' : 'light';
    this.layoutStore.changeTheme(nextTheme, true);

    this.userPreferenceService
      .get()
      .pipe(
        catchError(() => of<UserPreferenceModel | null>(null)),
        switchMap((preference) => {
          const payload = this.buildThemePayload(preference, nextTheme);
          return this.userPreferenceService.hasPreference(preference)
            ? this.userPreferenceService.update(payload)
            : this.userPreferenceService.create(payload);
        }),
        catchError(() => {
          this.toastr.error('Could not save theme preference.');
          return EMPTY;
        }),
      )
      .subscribe();
  }

  private buildThemePayload(
    preference: UserPreferenceModel | null,
    nextTheme: LayoutThemeType,
  ): UserPreferenceUpsertPayload {
    const themeMode: UserPreferenceThemeMode =
      nextTheme === 'dark' ? 'Dark' : 'Light';

    return {
      ...this.userPreferenceService.buildUpsertPayload(preference, {}),
      themeMode,
      accentColor:
        this.layoutStore.skin ?? preference?.accentColor ?? 'default',
    };
  }
}
